import {
  DocumentPrimitiveType,
  DocumentSchemaType,
  getSchemaObjectName,
  getSystemLabel,
  getSystemMetadataDefinition,
  IDocumentListSchema,
  IDocumentObjectSchema,
  IDocumentObjectSchemaProperty,
  IDocumentPrimitiveSchema,
  IDocumentSchema,
  isCompositeIdSchema,
  isLoopVariableSchema,
  isReferenceSchema,
  isUserDefinedSchema,
  ObjectSchemaTags
} from "../FlowDocument/FlowDocumentModel";
import { Node, ReactFlowInstance } from "reactflow";
import {
  BaseInputOutput,
  DataType,
  dataTypes,
  isObjectLike,
  isListType,
  primitiveTypes,
  isPrimitiveLike,
  isReferenceLike
} from "./TypeDefinitions";
import { getDsApiOutputs } from "./DataSourceApi/DataSourceAPiSpecs";
import {
  DocumentSchemaSource,
  getSourceSystem,
  IDataParam,
  INodeProperty,
  INodeTypeDefinition,
  ISchemaSystem
} from "./Base/NodeTypeDefinitions";
import { convertToDocument, upperSnakeToCamelCase } from "../FlowDocument/DocumentProcessor";
import { camelCaseToReadable } from "../Util/NodeUtil";
import { IFlowInputProperties } from "../FlowInputManagement";

export interface ISchemaSource {
  sourceSystem: string;
  sourceId: string;
}

export interface IDesignerSchema extends IDocumentSchema {
  source: ISchemaSource;
}

let dataSchemas: IDesignerSchema[] = [];

export const getSchema = (schemaId: string) => {
  const schema = dataSchemas.find((s: IDocumentSchema) => s.id === schemaId);
  if (schema) {
    return schema;
  }

  let convertedSchemaId = schemaId;
  if (schemaId) {
    const camelCaseSchemaId = upperSnakeToCamelCase(schemaId);
    if (primitiveTypes.some((dt: DataType) => dt === camelCaseSchemaId)) {
      convertedSchemaId = camelCaseSchemaId;
    }
  }
  return (
    dataSchemas.filter((s: IDocumentSchema) => s.id === convertedSchemaId)[0] ??
    (getEmptyObjectDocumentSchema() as unknown as IDesignerSchema)
  );
};

export const schemaExists = (schemaId: string) => {
  return dataSchemas.some((s: IDocumentSchema) => s.id === schemaId);
};

export const getListSchemaWithItemSchema = (schemaId: string) =>
  dataSchemas.find(
    (s: IDocumentSchema) =>
      s.schemaType === DocumentSchemaType.list &&
      ((s as IDocumentListSchema).listItemSchema === schemaId ||
        upperSnakeToCamelCase((s as IDocumentListSchema).listItemSchema) === schemaId)
  );

export const getSchemas = () => {
  return dataSchemas;
};

export interface IListSchemasOptions {
  excludeReferences?: boolean;
  referenceOnly?: boolean;
  includePrimitives?: boolean;
  includeLists?: boolean;
  userDefinedOnly?: boolean;
}

export interface IListSchemaRow {
  id: string;
  name: string;
  system: string;
  dataType: string;
}

export const getListSchemaRow = (
  schema: IDocumentSchema,
  includeListInDataTypeName: boolean = true
): IListSchemaRow => {
  const systemLabel = getSystemLabel(schema);
  const objectName = getSchemaObjectName(schema);
  let itemName = objectName !== "" ? objectName : schema.id;
  const dataType = getDataTypeFromSchema(schema.id);
  const idLabelCompositeSuffix = isCompositeIdSchema(schema) ? " (Composite ID)" : "";
  let dataTypeName = camelCaseToReadable(dataType) + idLabelCompositeSuffix;
  if (isReferenceLike(dataType as DataType)) {
    dataTypeName = "Reference";
  }
  if (!includeListInDataTypeName) {
    dataTypeName = dataTypeName.replace("List", "").trim();
  }
  return { id: schema.id, name: itemName, system: systemLabel, dataType: dataTypeName };
};

export const listSchemas = (options: IListSchemasOptions) => {
  return getSchemas()
    .filter(
      (schema: IDesignerSchema) =>
        !isLoopVariableSchema(schema) &&
        schema.id !== noReferenceSchemaId &&
        schema.id !== emptyObjectSchemaId &&
        schema.id !== listOfEmptyObjectSchemaId &&
        (!options?.excludeReferences || !isReferenceSchema(schema)) &&
        (!options?.referenceOnly || isReferenceSchema(schema)) &&
        (!options?.userDefinedOnly || isUserDefinedSchema(schema)) &&
        (options?.includePrimitives || !isPrimitiveLike(getDataTypeFromSchema(schema.id) as DataType)) &&
        (options?.includeLists || schema.schemaType !== DocumentSchemaType.list)
    )
    .map((schema: IDesignerSchema) => getListSchemaRow(schema));
};

export const setSchemas = (schemas: IDocumentSchema[]) => {
  dataSchemas = schemas.map((documentSchema: IDocumentSchema) => {
    return { ...documentSchema, source: { sourceSystem: "", sourceId: "" } };
  });
};

export const addSchema = (schema: IDocumentSchema, schemaSourceSystem: string, schemaSourceId: string) => {
  const designerSchema = { ...schema, source: { sourceSystem: schemaSourceSystem, sourceId: schemaSourceId } };
  const schemaIndex = dataSchemas.findIndex((s: IDesignerSchema) => s.id === schema.id);
  if (schemaIndex !== -1) {
    dataSchemas[schemaIndex] = designerSchema;
  } else {
    dataSchemas.push(designerSchema);
  }
};

export const addSchemas = (schemas: IDocumentSchema[], schemaSourceSystem: string, schemaSourceId: string) => {
  schemas.forEach((schema: IDocumentSchema) => addSchema(schema, schemaSourceSystem, schemaSourceId));
};

export const addObjectListSchemaWithItemSchema = (itemSchema: IDesignerSchema) => {
  if (itemSchema.schemaType === DocumentSchemaType.object) {
    const objectSchema = itemSchema as unknown as IDocumentObjectSchema & IDesignerSchema;
    const newListSchema: IDocumentListSchema & IDesignerSchema = {
      id: `${objectSchema.id}_LIST`,
      schemaType: DocumentSchemaType.list,
      listItemSchema: objectSchema.id,
      metadata: { ...objectSchema.metadata },
      source: { ...objectSchema.source }
    };
    const itemObjectSchema = itemSchema as unknown as IDocumentObjectSchema;
    if (itemObjectSchema.tags) {
      newListSchema.tags = [...itemObjectSchema.tags];
    }
    addSchema(newListSchema, objectSchema.source?.sourceSystem, objectSchema.source?.sourceId);
  }
};

export const addListSchemaIfNotExists = (itemSchemaId: string, node: Node, dataProperties: string[]) => {
  const hasObjectListInputs = Object.keys(node.data.nodeProperties.inputs).some(
    (inputName) =>
      dataProperties.includes(inputName) &&
      (node.data.nodeProperties.inputs[inputName].type === DataType.OBJECTLIST ||
        node.data.nodeProperties.inputs[inputName].type === DataType.OBJECTREFERENCELIST)
  );
  const hasObjectListOutputs = Object.keys(node.data.nodeProperties.outputs).some(
    (outputName) =>
      dataProperties.includes(outputName) &&
      (node.data.nodeProperties.outputs[outputName].type === DataType.OBJECTLIST ||
        node.data.nodeProperties.outputs[outputName].type === DataType.OBJECTREFERENCELIST)
  );

  if (hasObjectListInputs || hasObjectListOutputs) {
    const listSchema = getListSchemaWithItemSchema(itemSchemaId);
    if (!listSchema) {
      const selectedSchema = getSchema(itemSchemaId);
      // Create list schema if none exists for this object schema
      addObjectListSchemaWithItemSchema(selectedSchema);
    }
  }
};

export const noReferenceSchemaId = "fd_no_reference_schema";

export const noReferenceListSchemaId = "fd_list_of_no_reference_schema";

export const emptyObjectSchemaId = "fd_empty_schema";

export const listOfEmptyObjectSchemaId = "fd_list_of_empty_schema";

export const isEmptySchema = (schemaId: string) => {
  return (
    schemaId === emptyObjectSchemaId ||
    schemaId === listOfEmptyObjectSchemaId ||
    schemaId === noReferenceSchemaId ||
    schemaId === noReferenceListSchemaId
  );
};

export const getNoReferenceSchema = (): IDocumentObjectSchema => {
  return {
    id: noReferenceSchemaId,
    schemaType: DocumentSchemaType.object,
    properties: {},
    tags: [ObjectSchemaTags.standardObjectReference]
  };
};

export const getNoReferenceListSchema = (): IDocumentListSchema => {
  return {
    id: noReferenceListSchemaId,
    schemaType: DocumentSchemaType.list,
    listItemSchema: noReferenceSchemaId
  };
};

export const getEmptySchemaId = (dataType: DataType | string) => {
  switch (dataType) {
    case DataType.OBJECT:
      return emptyObjectSchemaId;
    case DataType.OBJECTLIST:
      return listOfEmptyObjectSchemaId;
    default:
      return emptyObjectSchemaId;
  }
};

export const getEmptySchema = (dataType: DataType | string) => {
  switch (dataType) {
    case DataType.OBJECT:
      return getEmptyObjectDocumentSchema();
    case DataType.OBJECTLIST:
      return getListOfEmptyObjectDocumentSchema();
    default:
      return getEmptyObjectDocumentSchema();
  }
};

export const getEmptyObjectDocumentSchema = (): IDocumentObjectSchema => {
  return {
    id: emptyObjectSchemaId,
    schemaType: DocumentSchemaType.object,
    properties: {}
  } as IDocumentObjectSchema;
};

export const getListOfEmptyObjectDocumentSchema = (): IDocumentListSchema => {
  return {
    id: listOfEmptyObjectSchemaId,
    schemaType: DocumentSchemaType.list,
    listItemSchema: emptyObjectSchemaId
  } as IDocumentListSchema;
};

// Added export for EnableInputsFormSection
export const getSchemaSource = (designerNode: Node<any>) => {
  const nodeDefinition: INodeTypeDefinition = globalThis.nodeTypeDefinitions.getDefinition(designerNode.type!)!;
  const schemaSourceProperty: INodeProperty = nodeDefinition.nodeConfigProperties.filter(
    (p: INodeProperty) => p.schemaSourceSystemType
  )[0]!;
  if (schemaSourceProperty && designerNode.data.nodeProperties[schemaSourceProperty.name]) {
    return {
      sourceSystem: schemaSourceProperty.schemaSourceSystemType!,
      sourceId: designerNode.data.nodeProperties[schemaSourceProperty.name]?.toString()
    };
  }

  return null;
};

export const updateSchemaSources = (designerNodes: Node<any>[]) => {
  dataSchemas.forEach((designerSchema: IDesignerSchema) => {
    let sourceSystem = "";
    let sourceId = "";

    designerNodes.forEach((designerNode: Node<any>) => {
      const schemaInInputs =
        Object.keys(designerNode.data.nodeProperties.inputs).filter(
          (inputName: string) => designerNode.data.nodeProperties.inputs[inputName].schemaId === designerSchema.id
        ).length > 0;
      const schemaInOutputs =
        Object.keys(designerNode.data.nodeProperties.outputs).filter(
          (outputName: string) => designerNode.data.nodeProperties.outputs[outputName].schemaId === designerSchema.id
        ).length > 0;

      const nodeType = designerNode.type ?? "";
      const nodeDefinition = globalThis.nodeTypeDefinitions.getDefinition(nodeType);
      const nodeConfigSectionCreatesIo = nodeDefinition?.nodeConfigProperties.some(
        (p: INodeProperty) => (p.schemaSystem as ISchemaSystem)?.createIo
      );

      if (schemaInInputs || schemaInOutputs) {
        const schemaSource = getSchemaSource(designerNode);
        if (schemaSource) {
          sourceSystem = getSourceSystem(schemaSource.sourceSystem);
          sourceId = schemaSource.sourceId;
        }
      }

      if (nodeConfigSectionCreatesIo) {
        const metadataDefinition = getSystemMetadataDefinition(designerSchema);
        const metadata = (designerSchema as unknown as IDocumentObjectSchema).metadata;

        // If the metadata definition has a schema source system and the metadata exists, use it
        if (metadataDefinition?.schemaSourceSystem && metadata) {
          sourceSystem = metadataDefinition.schemaSourceSystem;
          sourceId = metadata[metadataDefinition.objectIdProperty];
        } else {
          const schemaSource = getSchemaSource(designerNode);
          if (schemaSource) {
            sourceSystem = getSourceSystem(schemaSource.sourceSystem);
            sourceId = schemaSource.sourceId;
          }
        }
      }
    });

    let isPrimitiveSchema = designerSchema.schemaType === DocumentSchemaType.primitive;
    if (designerSchema.schemaType === DocumentSchemaType.list) {
      const itemSchema = dataSchemas.find(
        (ds: IDesignerSchema) => ds.id === (designerSchema as unknown as IDocumentListSchema).listItemSchema
      );
      if (itemSchema && itemSchema.schemaType === DocumentSchemaType.primitive) {
        isPrimitiveSchema = true;
      }
    }

    const isEmptyUndefined =
      designerSchema.id === noReferenceSchemaId ||
      designerSchema.id === emptyObjectSchemaId ||
      designerSchema.id === listOfEmptyObjectSchemaId;

    if (!isPrimitiveSchema && !isEmptyUndefined) {
      designerSchema.source.sourceSystem = sourceSystem;
      designerSchema.source.sourceId = sourceId;
      setSchemaSourceRecurse(designerSchema, sourceSystem, sourceId);
    }
  });
};

const setSchemaSourceRecurse = (
  schema: IDesignerSchema,
  sourceSystem: string,
  sourceId: string,
  visitedSchemaIds: Set<string> = new Set()
) => {
  if (schema.schemaType === DocumentSchemaType.primitive) {
    return;
  }

  // Guard against self/mutually-referential schemas causing infinite recursion
  if (visitedSchemaIds.has(schema.id)) {
    return;
  }
  visitedSchemaIds.add(schema.id);

  schema.source.sourceSystem = sourceSystem;
  schema.source.sourceId = sourceId;
  if (schema.schemaType === DocumentSchemaType.object) {
    const objectSchema: any = schema;
    Object.keys(objectSchema.properties).forEach((propertyName: string) => {
      setSchemaSourceRecurse(
        getSchema(objectSchema.properties[propertyName]!.schema)!,
        sourceSystem,
        sourceId,
        visitedSchemaIds
      );
    });
  }
  if (schema.schemaType === DocumentSchemaType.list) {
    const listSchema: IDocumentListSchema = schema as any;
    const itemSchema: IDesignerSchema = getSchema(listSchema.listItemSchema)!;
    if (itemSchema.schemaType === DocumentSchemaType.object) {
      if (!itemSchema.source) {
        itemSchema.source = { sourceId: sourceId, sourceSystem: sourceSystem };
      }
      itemSchema.source.sourceSystem = sourceSystem;
      itemSchema.source.sourceId = sourceId;
      const objectSchema: IDocumentObjectSchema = itemSchema as any;
      Object.keys(objectSchema.properties).forEach((propertyName: string) => {
        setSchemaSourceRecurse(
          getSchema(objectSchema.properties[propertyName]!.schema!)!,
          sourceSystem,
          sourceId,
          visitedSchemaIds
        );
      });
    }
  }
};

export const getDataTypeFromSchema = (schemaId: string) => {
  const schema: IDesignerSchema = getSchema(schemaId)!;
  return getDataTypeFromSchemaInstance(schema);
};

export const getDataTypeFromSchemaInstance = (schema: IDesignerSchema) => {
  const schemaId = schema.id;
  const isPrimitiveList = dataTypes.indexOf(schemaId.replace("_List", "")) !== -1;

  if (isPrimitiveList) {
    return schemaId.replace("_List", "List");
  }

  if (isReferenceSchema(schema)) {
    if (schema.schemaType === DocumentSchemaType.list) {
      return DataType.OBJECTREFERENCELIST;
    }
    return "objectReference";
  }

  switch (schema.schemaType) {
    case DocumentSchemaType.primitive:
      return schemaId;
    case DocumentSchemaType.object:
      return "object";
    case DocumentSchemaType.list:
      return "objectList";
  }
};

export const getPrimitiveSchemas = () => {
  const schemas: IDocumentSchema[] = [];

  Object.keys(DocumentPrimitiveType).forEach((primitiveSchemaId: string) => {
    const primitiveSchema: IDocumentPrimitiveSchema = {
      id: primitiveSchemaId,
      schemaType: DocumentSchemaType.primitive,
      primitiveType: (DocumentPrimitiveType as any)[primitiveSchemaId]
    };
    schemas.push(primitiveSchema);

    const primitiveListSchema: IDocumentListSchema = {
      id: `${primitiveSchemaId}_List`,
      schemaType: DocumentSchemaType.list,
      listItemSchema: <DocumentPrimitiveType>primitiveSchemaId
    };
    schemas.push(primitiveListSchema);
  });

  return schemas;
};

export const getSchemaId = (
  designerDataType: string,
  node: Node<any> | undefined = undefined,
  objectPath: string[] | undefined = undefined
) => {
  const dataType = designerDataType;
  const isPrimitive = Object.keys(DocumentPrimitiveType).indexOf(dataType.replace("List", "")) !== -1;
  const isObject = isObjectLike(dataType as DataType);
  const isList = isListType(dataType as DataType);

  if (isPrimitive) {
    return `${dataType.replace("List", "")}${isList ? "_List" : ""}`;
  }

  if (designerDataType === DataType.OBJECTREFERENCE) {
    return noReferenceSchemaId;
  }

  if (designerDataType === DataType.OBJECTREFERENCELIST) {
    return noReferenceListSchemaId;
  }

  if (node && isObject) {
    const propertyObjectPath = objectPath ?? [];
    const propertyPath = Array.isArray(propertyObjectPath) ? propertyObjectPath.join("_") : propertyObjectPath;
    const schemaSource = getSchemaSource(node);
    const schemaId = schemaSource
      ? `${schemaSource?.sourceSystem}_${schemaSource?.sourceId}_${propertyPath}${isList ? "_List" : ""}`
      : "";

    return schemaId;
  }

  throw new Error(`Cannot get document schema for ${dataType}.`);
};

export const getLoopVariableSchemaId = (nodeId: string) => {
  return `${DocumentSchemaSource.userDefined}_${nodeId}_LOOP_VARIABLES`;
};

export const getObjectSchemaIdWithSource = (
  designerDataType: string,
  objectPath: string[] | undefined = undefined,
  schemaSource: {
    sourceSystem: DocumentSchemaSource;
    sourceId: any;
  }
) => {
  const dataType = designerDataType;
  const isObject = isObjectLike(dataType as DataType);
  const isList = isListType(dataType as DataType);

  if (isObject || designerDataType === DataType.OBJECTREFERENCE) {
    const propertyObjectPath = objectPath ?? [];
    const propertyPath = Array.isArray(propertyObjectPath) ? propertyObjectPath.join("_") : propertyObjectPath;
    const schemaId = schemaSource
      ? `${schemaSource?.sourceSystem}_${schemaSource?.sourceId}_${propertyPath}${isList ? "_List" : ""}`
      : "";

    return schemaId;
  }

  throw new Error(`Cannot get document schema for ${dataType}.`);
};

export interface IGetChildSchemasResult {
  hasChildSchemaCycle: boolean;
  cycleChildSchemaId?: string;
}

export const getChildSchemaIdsRecursive = (
  parentSchemaId: string,
  schemas: IDocumentSchema[],
  outChildSchemaIds: string[],
  visitedSchemaIds: string[] = []
): IGetChildSchemasResult => {
  const parentSchema = schemas.find((schema: IDocumentSchema) => schema.id === parentSchemaId);

  if (parentSchema) {
    let objectSchema: IDocumentObjectSchema | undefined;

    if (!outChildSchemaIds.includes(parentSchemaId)) {
      outChildSchemaIds.push(parentSchemaId);
    }

    if (parentSchema.schemaType === DocumentSchemaType.object) {
      objectSchema = parentSchema as IDocumentObjectSchema;
    }
    if (parentSchema.schemaType === DocumentSchemaType.list) {
      const objectSchemaId = (parentSchema as IDocumentListSchema).listItemSchema;
      const listItemSchema = schemas.find((schema: IDocumentSchema) => schema.id === objectSchemaId);
      if (listItemSchema && !outChildSchemaIds.includes(listItemSchema.id)) {
        outChildSchemaIds.push(listItemSchema.id);
      }
      if (listItemSchema?.schemaType === DocumentSchemaType.object) {
        objectSchema = listItemSchema as IDocumentObjectSchema;
      }
    }

    if (objectSchema) {
      if (visitedSchemaIds.includes(objectSchema.id)) {
        return { hasChildSchemaCycle: true, cycleChildSchemaId: objectSchema.id };
      }

      let propertyChildSchemaResult: IGetChildSchemasResult = { hasChildSchemaCycle: false };
      visitedSchemaIds.push(objectSchema.id);
      Object.keys(objectSchema.properties).forEach((propertyName: string) => {
        const schemaProperty = objectSchema?.properties[propertyName];
        if (schemaProperty) {
          const getChildSchemasResult = getChildSchemaIdsRecursive(schemaProperty.schema, schemas, outChildSchemaIds, [
            ...visitedSchemaIds
          ]);
          if (getChildSchemasResult.hasChildSchemaCycle) {
            propertyChildSchemaResult = getChildSchemasResult;
          }
        }
      });

      if (propertyChildSchemaResult.hasChildSchemaCycle) {
        return propertyChildSchemaResult;
      }
    }
  }

  return { hasChildSchemaCycle: false };
};

export const addObjectSchemaForNode = (
  node: Node<any>,
  objectPath: string[] | undefined = undefined,
  schemaSourceSystem: string,
  schemaSourceId: string
): IDocumentObjectSchema => {
  const schema: IDocumentObjectSchema = getObjectSchema(dataSchemas, node, objectPath);
  const designerSchema = { ...schema, source: { sourceSystem: schemaSourceSystem, sourceId: schemaSourceId } };
  const schemaIndex = dataSchemas.findIndex((s: IDesignerSchema) => s.id === schema.id);
  if (schemaIndex !== -1) {
    dataSchemas[schemaIndex] = designerSchema;
  } else {
    dataSchemas.push(designerSchema);
  }
  return addObjectSchema(schema, schemaSourceSystem, schemaSourceId);
};

export const addObjectReferenceSchema = (
  schema: IDocumentObjectSchema,
  schemaSourceSystem: string,
  schemaSourceId: string
): IDocumentObjectSchema => {
  const designerSchema = { ...schema, source: { sourceSystem: schemaSourceSystem, sourceId: schemaSourceId } };
  const schemaIndex = dataSchemas.findIndex((s: IDesignerSchema) => s.id === schema.id);
  if (schemaIndex !== -1) {
    dataSchemas[schemaIndex] = designerSchema;
  } else {
    dataSchemas.push(designerSchema);
  }
  return schema;
};

export const addObjectSchema = (
  schema: IDocumentObjectSchema,
  schemaSourceSystem: string,
  schemaSourceId: string
): IDocumentObjectSchema => {
  const designerSchema = { ...schema, source: { sourceSystem: schemaSourceSystem, sourceId: schemaSourceId } };
  const schemaIndex = dataSchemas.findIndex((s: IDesignerSchema) => s.id === schema.id);
  if (schemaIndex !== -1) {
    dataSchemas[schemaIndex] = designerSchema;
  } else {
    dataSchemas.push(designerSchema);
  }
  return schema;
};

export const deleteSchema = (schemaId: string) => {
  dataSchemas = dataSchemas.filter(
    (s: IDesignerSchema) => s.id !== schemaId && s.id !== getListSchemaWithItemSchema(schemaId)?.id
  );
};

export const getObjectSchema = (
  schemas: IDocumentSchema[],
  node: Node<any>,
  objectPath: string[] | undefined = undefined
): IDocumentObjectSchema => {
  const nodeObjectPath: string[] = objectPath ?? [];
  const schemaSource = getSchemaSource(node);

  let designerOutputs: BaseInputOutput[] = [];

  if (schemaSource) {
    if (schemaSource.sourceSystem === DocumentSchemaSource.plexOpenApi) {
      //designerOutputs = getApiOutputs(category, apiTitle, apiAction, currentTargetDTO, nodeObjectPath);
    } else if (schemaSource.sourceSystem === DocumentSchemaSource.dataSource) {
      const forOutput = nodeObjectPath.indexOf("outputs") !== -1;
      designerOutputs = getDsApiOutputs("Commands", schemaSource.sourceId, !forOutput, forOutput);
    }
  }

  const documentOutputs: { [name: string]: IDocumentObjectSchemaProperty } = {};
  designerOutputs.forEach((o: BaseInputOutput) => {
    const outputSchemaId = getSchemaId(o.type, node);
    documentOutputs[o.name] = {
      schema: outputSchemaId,
      required: o.required
    };

    updateSchemas(schemas, node, o.type, objectPath?.concat([o.name]));
  });

  return {
    id: getSchemaId("object", node, objectPath),
    schemaType: DocumentSchemaType.object,
    properties: documentOutputs
  };
};

export const getDocumentObjectSchemaFromProperties = (id: string, properties: IDataParam[]): IDocumentObjectSchema => {
  const propertyObject: { [name: string]: IDocumentObjectSchemaProperty } = {};
  properties.forEach(
    (p: IDataParam) =>
      (propertyObject[p.name] = {
        schema: p.schemaId ?? "",
        required: p.required ?? false,
        label: p.label
      })
  );
  return {
    id: id,
    schemaType: DocumentSchemaType.object,
    properties: propertyObject
  };
};

export const addObjectListSchemaForNode = (
  node: Node<any>,
  objectPath: string[] | undefined = undefined,
  schemaSourceSystem: string,
  schemaSourceId: string
) => {
  const schema: IDocumentListSchema = getObjectListSchema(dataSchemas, node, objectPath);
  return addObjectListSchema(schema, schemaSourceSystem, schemaSourceId);
};

export const addObjectListSchema = (
  schema: IDocumentListSchema,
  schemaSourceSystem: string,
  schemaSourceId: string
) => {
  const designerSchema = { ...schema, source: { sourceSystem: schemaSourceSystem, sourceId: schemaSourceId } };
  const schemaIndex = dataSchemas.findIndex((s: IDesignerSchema) => s.id === schema.id);
  if (schemaIndex !== -1) {
    dataSchemas[schemaIndex] = designerSchema;
  } else {
    dataSchemas.push(designerSchema);
  }
  return schema;
};

export const getObjectListSchema = (
  schemas: IDocumentSchema[],
  node: Node<any>,
  objectPath: string[] | undefined = undefined
) => {
  const schemaId: string = getSchemaId("objectList", node, objectPath);
  const itemDataProperty = "object";
  const itemSchemaId: string = getSchemaId(itemDataProperty, node, objectPath).replace("_List", "");

  updateSchemas(schemas, node, itemDataProperty, objectPath);

  return {
    id: schemaId,
    schemaType: DocumentSchemaType.list,
    listItemSchema: itemSchemaId
  };
};

export const updateSchemas = (
  schemas: IDocumentSchema[],
  designerNode: Node<any>,
  designerDataType: string,
  objectPath: string[] | undefined = undefined
) => {
  const schemaId: string = getSchemaId(designerDataType, designerNode, objectPath);
  if (schemas.filter((s: IDocumentSchema) => s.id === schemaId).length === 0) {
    if (isObjectLike(designerDataType as DataType)) {
      if (isListType(designerDataType as DataType)) {
        schemas.push(getObjectListSchema(schemas, designerNode, objectPath));
      } else {
        schemas.push(getObjectSchema(schemas, designerNode, objectPath));
      }
    }
  }
};

export const setInputsFromSchema = (node: Node<any>, schema: IDocumentObjectSchema, required: boolean = false) => {
  Object.keys(schema.properties).forEach((propertyName) => {
    const property = schema.properties[propertyName]!;
    node.data.nodeProperties.inputs[propertyName] = {
      name: propertyName,
      schemaId: property.schema,
      type: getDataTypeFromSchema(property.schema),
      label: property.label,
      required: required,
      enabled: required
    } as BaseInputOutput;
  });
};

export const setObjectSchemas = (
  schemaId: string,
  dataPropertyNames: string[],
  dataProperties: IDataParam[]
): string[] => {
  const errors: string[] = [];
  dataPropertyNames.forEach((dataPropertyName) => {
    const dataProperty = dataProperties[dataPropertyName];
    if (dataProperty) {
      const schema = getSchema(schemaId);
      switch (dataProperty.type) {
        case DataType.OBJECT:
        case DataType.OBJECTREFERENCE:
          if (schema.schemaType === DocumentSchemaType.list) {
            dataProperty.schemaId = (schema as unknown as IDocumentListSchema).listItemSchema;
          } else {
            dataProperty.schemaId = schemaId;
          }
          break;
        case DataType.OBJECTLIST:
        case DataType.OBJECTREFERENCELIST:
          if (schema.schemaType === DocumentSchemaType.list) {
            dataProperty.schemaId = schemaId;
          } else {
            const listSchemaId = getListSchemaWithItemSchema(schemaId)?.id;
            dataProperty.schemaId = listSchemaId ?? listOfEmptyObjectSchemaId;
            if (!listSchemaId) {
              errors.push(`No list schema found for item schema '${getSchemaObjectName(getSchema(schemaId))}'.`);
            }
          }
          break;
      }
    }
  });

  return errors;
};

export const getSchemasInUse = (
  experimentalModeState: boolean,
  flowInputs: IFlowInputProperties[],
  reactFlowInstance: ReactFlowInstance,
  pruneUserSchemas: boolean
) => {
  // Check the current document with prune user schemas set to true, to see if user schema is being referenced from inputs or outputs. If it's being referenced, we should not allow deletion.
  const currentDocument = convertToDocument(experimentalModeState, flowInputs, reactFlowInstance, pruneUserSchemas);
  return currentDocument.schemas;
};
